/**
 * Import Graph Page
 * Issue #460: File-level import dependency visualization
 * Issue #501: Removed 500-file limit; added configure screen, async generation with live progress.
 * Issue #598: Removed full graph view — Explorer/List/Matrix/Tree views are sufficient.
 * Issue #599: Added Subgraph tab — focused import-neighbourhood view via ImportFocusedSubgraph.
 *
 * Phase flow:
 *   loading → ready (cached)
 *   loading → configure (no cache) → generating → ready
 *   ready → configure ("Re-analyze") → generating → ready
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import {
  ROLE_COLORS,
  type ImportGraphData,
  FileExplorer,
  FileList,
  ImportMatrix,
  FileTreeView,
  ImportFocusedSubgraph,
} from '../components/diagram/renderers/importGraph';

type ImportViewMode = 'explorer' | 'list' | 'matrix' | 'tree' | 'subgraph';

type PagePhase = 'loading' | 'configure' | 'generating' | 'ready' | 'error';

interface ScanResult {
  projectId: string;
  totalFiles: number;
  directories: Array<{ path: string; fileCount: number }>;
  activeExcludePatterns: string[];
}

interface JobProgress {
  filesCompleted: number;
  totalFiles: number;
  message: string;
  pct: number;
}

export function ImportGraphPage() {
  const { projectId } = useParams<{ projectId: string }>();

  const [phase, setPhase] = useState<PagePhase>('loading');
  const [graphData, setGraphData] = useState<ImportGraphData | null>(null);
  const [scanData, setScanData] = useState<ScanResult | null>(null);
  const [jobProgress, setJobProgress] = useState<JobProgress>({ filesCompleted: 0, totalFiles: 0, message: '', pct: 0 });
  const [error, setError] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  // Issue #566: search lifted to page level so it persists across view tabs
  const [searchQuery, setSearchQuery] = useState('');
  // Issue #562: multi-select role filter (Set of role strings) — persists across view tabs
  const [roleFilters, setRoleFilters] = useState<Set<string>>(new Set());
  const [showCircularOnly, setShowCircularOnly] = useState(false);
  const [viewMode, setViewMode] = useState<ImportViewMode>('explorer');
  const [viewHistory, setViewHistory] = useState<ImportViewMode[]>([]);
  // Issue #599: Subgraph tab state
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const [focusedNodeHistory, setFocusedNodeHistory] = useState<string[]>([]);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stop polling on unmount
  useEffect(() => () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); }, []);

  // On mount: try cache first
  useEffect(() => {
    if (!projectId) return;
    api.getImportGraph(projectId).then(data => {
      setGraphData(data);
      setPhase('ready');
    }).catch(() => {
      // No cache — show configure screen
      runScan();
    });
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const runScan = useCallback(async () => {
    if (!projectId) return;
    setPhase('loading');
    setError(null);
    try {
      const scan = await api.scanImportGraphFiles(projectId);
      setScanData(scan);
      setPhase('configure');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to scan files');
      setPhase('error');
    }
  }, [projectId]);

  const startGeneration = useCallback(async () => {
    if (!projectId) return;
    setPhase('generating');
    setJobProgress({ filesCompleted: 0, totalFiles: scanData?.totalFiles ?? 0, message: 'Starting…', pct: 0 });

    let jobId: string;
    try {
      const res = await api.generateImportGraph(projectId);
      jobId = res.jobId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start generation');
      setPhase('error');
      return;
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const status = await api.getImportGraphJobStatus(projectId, jobId);
        setJobProgress({
          filesCompleted: status.filesCompleted,
          totalFiles: status.totalFiles || scanData?.totalFiles || 0,
          message: status.message,
          pct: status.progress,
        });

        if (status.status === 'complete') {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          setGraphData(status.result as ImportGraphData);
          setPhase('ready');
        } else if (status.status === 'error') {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          setError(status.error ?? 'Generation failed');
          setPhase('error');
        }
      } catch {
        // transient poll error — keep trying
      }
    }, 1500);
  }, [projectId, scanData]);

  // Issue #599: subgraph navigation handlers
  const handleOpenSubgraph = useCallback((fileId: string) => {
    setFocusedNodeId(fileId);
    setFocusedNodeHistory([]);
    setViewHistory(h => [...h, viewMode].slice(-5));
    setViewMode('subgraph');
  }, [viewMode]);

  const handleFocusNodeNavigate = useCallback((fileId: string) => {
    setFocusedNodeHistory(prev => focusedNodeId ? [...prev, focusedNodeId] : prev);
    setFocusedNodeId(fileId);
  }, [focusedNodeId]);

  const handleFocusBack = useCallback(() => {
    if (focusedNodeHistory.length > 0) {
      const prev = focusedNodeHistory[focusedNodeHistory.length - 1];
      setFocusedNodeHistory(h => h.slice(0, -1));
      setFocusedNodeId(prev);
    } else {
      // Go back to previous view
      setViewMode(viewHistory[viewHistory.length - 1] ?? 'explorer');
      setViewHistory(h => h.slice(0, -1));
    }
  }, [focusedNodeHistory, viewHistory]);

  const handleFocusNavigateToIndex = useCallback((i: number) => {
    const target = focusedNodeHistory[i];
    if (target) {
      setFocusedNodeHistory(prev => prev.slice(0, i));
      setFocusedNodeId(target);
    }
  }, [focusedNodeHistory]);

  const handleFocusClose = useCallback(() => {
    setViewMode(viewHistory[viewHistory.length - 1] ?? 'explorer');
    setViewHistory(h => h.slice(0, -1));
  }, [viewHistory]);

  const roles = useMemo(() => {
    if (!graphData) return [];
    const s = new Set<string>();
    for (const n of graphData.graph.nodes) s.add(n.role);
    return Array.from(s).sort();
  }, [graphData]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          <p className="text-slate-600">Loading import dependencies…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Error</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <div className="flex gap-3 justify-center">
            <Link to={`/projects/${projectId}`} className="px-4 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50">
              Back to Project
            </Link>
            <button onClick={runScan} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Configure ────────────────────────────────────────────────────────────
  if (phase === 'configure' && scanData) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to={`/projects/${projectId}`} className="text-slate-500 hover:text-slate-700">&larr; Back to Project</Link>
            <h1 className="text-xl font-semibold text-slate-800">Import Dependencies</h1>
          </div>
        </header>

        <div className="flex-1 flex items-start justify-center pt-16 px-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 w-full max-w-lg">
            <div className="px-6 py-5 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-800">Ready to Analyze</h2>
              <p className="text-slate-500 text-sm mt-1">
                Found <strong className="text-slate-800">{scanData.totalFiles.toLocaleString()}</strong> source files
              </p>
            </div>

            {/* Directory breakdown */}
            <div className="px-6 py-4 max-h-64 overflow-y-auto">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">Files by directory</p>
              <div className="space-y-1.5">
                {scanData.directories.map(d => (
                  <div key={d.path} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-slate-700">{d.path}/</span>
                    <span className="text-slate-400 tabular-nums">{d.fileCount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Active exclusion patterns */}
            {scanData.activeExcludePatterns.length > 0 && (
              <div className="px-6 py-3 border-t border-slate-100 bg-slate-50">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Active exclusions</p>
                <div className="flex flex-wrap gap-1.5">
                  {scanData.activeExcludePatterns.map(p => (
                    <code key={p} className="text-xs bg-white border border-slate-200 px-2 py-0.5 rounded text-slate-600">{p}</code>
                  ))}
                </div>
                <Link
                  to={`/projects/${projectId}`}
                  className="text-xs text-blue-600 hover:underline mt-1.5 inline-block"
                >
                  Edit in project settings →
                </Link>
              </div>
            )}

            {scanData.activeExcludePatterns.length === 0 && (
              <div className="px-6 py-3 border-t border-slate-100">
                <p className="text-xs text-slate-400">
                  No extra exclusions configured.{' '}
                  <Link to={`/projects/${projectId}`} className="text-blue-600 hover:underline">
                    Add exclusion patterns in project settings
                  </Link>
                  {' '}to reduce analysis scope.
                </p>
              </div>
            )}

            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3">
              <Link
                to={`/projects/${projectId}`}
                className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50"
              >
                Cancel
              </Link>
              <button
                onClick={startGeneration}
                className="px-5 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Generate Import Graph
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Generating ───────────────────────────────────────────────────────────
  if (phase === 'generating') {
    const { filesCompleted, totalFiles, message, pct } = jobProgress;
    const displayPct = Math.min(100, Math.round(pct));
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 w-full max-w-md px-8 py-10 flex flex-col items-center gap-6">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <div className="w-full">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-700 font-medium">
                {totalFiles > 0
                  ? `Parsing files: ${filesCompleted.toLocaleString()} / ${totalFiles.toLocaleString()}`
                  : 'Analyzing…'}
              </span>
              <span className="text-slate-400">{displayPct}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${displayPct}%` }}
              />
            </div>
            {message && (
              <p className="text-xs text-slate-400 mt-2 truncate" title={message}>{message}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Ready ────────────────────────────────────────────────────────────────
  if (!graphData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-600">No import graph data available</p>
      </div>
    );
  }

  const VIEW_TABS: { id: ImportViewMode; label: string }[] = [
    { id: 'explorer', label: 'Explorer' },
    { id: 'list', label: 'List' },
    { id: 'matrix', label: 'Matrix' },
    { id: 'tree', label: 'Tree' },
    { id: 'subgraph', label: 'Subgraph' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header — top row: title + view tabs + action buttons */}
      <header className="bg-white border-b border-slate-200">
        <div className="px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to={`/projects/${projectId}`} className="text-slate-500 hover:text-slate-700 flex-shrink-0">
              &larr; Back
            </Link>
            <h1 className="text-lg font-semibold text-slate-800 truncate">
              Import Graph: {graphData.projectName}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Issue #570 P3: View tab back button */}
            {viewHistory.length > 0 && (
              <button
                onClick={() => {
                  const prev = viewHistory[viewHistory.length - 1];
                  setViewHistory(h => h.slice(0, -1));
                  setViewMode(prev);
                }}
                className="px-2 py-1.5 text-sm text-slate-500 hover:text-slate-700 rounded"
                title="Back to previous view"
              >
                ← Back
              </button>
            )}
            {/* View mode tabs */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              {VIEW_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setViewHistory(h => [...h, viewMode].slice(-5));
                    setViewMode(tab.id);
                  }}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    viewMode === tab.id
                      ? 'bg-white shadow text-blue-600 font-medium'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button
              onClick={runScan}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              Re-analyze
            </button>
          </div>
        </div>

        {/* Shared filter toolbar — shown for all view tabs (issue #566) */}
        {/* Dimmed when Explorer or Subgraph tab is active — filters are not used by those views */}
        <div className={`px-6 py-2 border-t border-slate-100 bg-slate-50 flex items-center gap-3 flex-wrap text-sm transition-opacity ${(viewMode === 'subgraph' || viewMode === 'explorer') ? 'opacity-40 pointer-events-none' : ''}`}>
          {/* Search — persists across all view tabs */}
          <input
            type="text"
            placeholder="Search files…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="px-2.5 py-1.5 border border-slate-300 bg-white rounded text-sm w-48"
          />

          {/* Role filter chips */}
          <div className="flex items-center gap-1 flex-wrap">
            {roles.map(r => {
              const active = roleFilters.has(r);
              const color = ROLE_COLORS[r] ?? '#94a3b8';
              return (
                <button
                  key={r}
                  onClick={() => setRoleFilters(prev => {
                    const next = new Set(prev);
                    if (next.has(r)) next.delete(r); else next.add(r);
                    return next;
                  })}
                  className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${active ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
                  style={active ? { backgroundColor: color, borderColor: color } : {}}
                  title={`Filter by role: ${r}`}
                >
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              );
            })}
          </div>

          {/* Circular chip — styled as pill to match role chips */}
          <button
            onClick={() => setShowCircularOnly(v => !v)}
            className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${showCircularOnly ? 'text-white border-transparent bg-red-500' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
            title="Show only files in circular dependency chains"
          >
            Circular
          </button>

          {(searchQuery || roleFilters.size > 0 || showCircularOnly) && (
            <button
              onClick={() => { setSearchQuery(''); setRoleFilters(new Set()); setShowCircularOnly(false); }}
              className="px-2 py-1 text-xs text-blue-600 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Explain why filters are disabled on Explorer and Subgraph tabs */}
        {(viewMode === 'subgraph' || viewMode === 'explorer') && (
          <div className="px-6 py-1.5 border-t border-slate-100 bg-slate-50 text-xs text-slate-500 italic">
            {viewMode === 'subgraph'
              ? 'Filters apply to Graph, List, Matrix and Tree views. Use the subgraph\'s own controls (direction, depth, hide barrels) for this view.'
              : 'Filters apply to Graph, List, Matrix and Tree views. The Explorer navigates the full graph regardless of active filters.'}
          </div>
        )}
      </header>

      {/* Stats bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-2 flex items-center gap-6 text-sm flex-wrap">
        <span className="text-slate-600">
          <strong>{graphData.metadata.totalFiles}</strong> files
        </span>
        <span className="text-slate-600">
          <strong>{graphData.graph.edges.length}</strong> imports
        </span>
        <span className="text-slate-600">
          <strong>{graphData.metadata.entryPointCount}</strong> entry points
        </span>
        {graphData.metadata.circularDependencyCount > 0 && (
          <span className="text-red-600">
            <strong>{graphData.metadata.circularDependencyCount}</strong> circular
          </span>
        )}
        <span className="text-slate-600">
          <strong>{graphData.metadata.barrelFileCount}</strong> barrels
        </span>
        {graphData.cachedAt && (
          <span className="text-slate-400 text-xs">
            Cached {new Date(graphData.cachedAt).toLocaleString()}
          </span>
        )}
        <div className="ml-auto flex items-center gap-4">
          {Object.entries(ROLE_COLORS).slice(0, 5).map(([role, color]) => (
            <span key={role} className="flex items-center gap-1">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
              <span className="text-xs text-slate-500 capitalize">{role}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Issue #570: Always mounted — use hidden class so local state persists across tab switches */}
        <div className={viewMode === 'explorer' ? 'flex-1 overflow-hidden' : 'hidden'}>
          <FileExplorer
            data={graphData}
            selectedFileId={selectedNodeId ?? undefined}
            onSelectFile={setSelectedNodeId}
            onViewSubgraph={handleOpenSubgraph}
          />
        </div>
        <div className={viewMode === 'list' ? 'flex-1 overflow-hidden' : 'hidden'}>
          <FileList
            data={graphData}
            selectedFileId={selectedNodeId ?? undefined}
            onSelectFile={setSelectedNodeId}
            onViewInExplorer={(id) => { setSelectedNodeId(id); setViewMode('explorer'); }}
            onViewSubgraph={handleOpenSubgraph}
            searchQuery={searchQuery}
            roleFilters={roleFilters}
            showCircularOnly={showCircularOnly}
          />
        </div>
        <div className={viewMode === 'matrix' ? 'flex-1 overflow-hidden' : 'hidden'}>
          <ImportMatrix
            data={graphData}
            onSelectFile={(id) => { setSelectedNodeId(id); setViewMode('explorer'); }}
            searchQuery={searchQuery}
            roleFilters={roleFilters}
          />
        </div>
        <div className={viewMode === 'tree' ? 'flex-1 overflow-hidden' : 'hidden'}>
          <FileTreeView
            data={graphData}
            selectedFileId={selectedNodeId ?? undefined}
            onSelectFile={setSelectedNodeId}
            onViewInExplorer={(id) => { setSelectedNodeId(id); setViewMode('explorer'); }}
            onViewSubgraph={handleOpenSubgraph}
            searchQuery={searchQuery}
          />
        </div>

        {/* Issue #599: Subgraph tab */}
        {viewMode === 'subgraph' && (
          <div className="flex-1 overflow-hidden">
            {focusedNodeId ? (
              <ImportFocusedSubgraph
                data={graphData}
                rootFileId={focusedNodeId}
                historyFileIds={focusedNodeHistory}
                onSelectFile={handleFocusNodeNavigate}
                onBack={handleFocusBack}
                onClose={handleFocusClose}
                onNavigateToHistoryIndex={handleFocusNavigateToIndex}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 text-slate-500 h-full">
                <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <div className="text-center">
                  <p className="font-medium text-slate-700">No file selected</p>
                  <p className="text-sm mt-1">Open <strong>List</strong> or <strong>Tree</strong> view and click <em>View subgraph</em> on a file.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
